import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import StorageService from "../../../App/StorageService"
import { RoleType } from "../../../App/enums"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import { RootState } from "../../../App/store"
import { PatchOrganizationTutorialProgressRequest } from "../../Organization/OrganizationSettings/OrganizationAccountServiceRequest"
import { CloseIcon } from "../../Shared/Components/SvgIcons"
import Button from "../../Shared/Primitive/Button"
import { setModalData } from "../../Shared/SharedSlices/ModalSlice"
import { setOrganizationData } from "../../Shared/SharedSlices/OrganizationsSlice"
import { setUserData } from "../../Shared/SharedSlices/UserDataSlice"
import { getTutorialStepDefinition } from "../../Shared/Onboarding/TutorialFlow"
import { updateUserTutorialProgressRequest } from "../UserProfile/UserTutorialProgressRequest"
import { useLocation, useNavigate } from "react-router"

function ShowTutorialModal() {

    const dispatch = useDispatch()
    const translation = useSelector(selectTranslations)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const userData = useSelector((state: RootState) => state.userDataSlice)
    const orgData = useSelector((state: RootState) => state.organizationsSlice.organizationSlice.organization)
    const navigate = useNavigate()
    const location = useLocation()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const context = useMemo(() => ({ organizationId: orgData.id }), [orgData.id])
    const defaultStartStep = 1
    const defaultStartRoute = useMemo(() => getTutorialStepDefinition(role, defaultStartStep)?.fallbackRoute?.(context), [context, role])

    const closeModal = () => {
        dispatch(setModalData({ open: false, showTutorial: false }))

    }

    const updateTutorialProgress = async (step: number) => {
        switch (role) {
            case RoleType.USER:
                await updateUserTutorialProgressRequest(userData, { tutorial_progress: step })
                dispatch(
                    setUserData({
                        ...userData,
                        tutorial_progress: step,
                    })
                )
                break
            case RoleType.ORGANIZATION:
                await PatchOrganizationTutorialProgressRequest(orgData.id!, step)
                dispatch(
                    setOrganizationData({
                        ...orgData,
                        tutorial_progress: step,
                    })
                )
                break
            default:
                break
        }
    }

    const startWith = async (step: number, route?: string) => {
        if (isSubmitting) {
            return
        }

        setIsSubmitting(true)

        await updateTutorialProgress(step)

        if (route && route !== `${location.pathname}${location.search}`) {
            navigate(route)
        }

        setIsSubmitting(false)
        closeModal()
    }

    const handleStartHere = () => {
        startWith(defaultStartStep, defaultStartRoute)
    }

    const handleSkip = () => {
        startWith(100)
    }

    useEffect(() => {
        if(Object.keys(userData).length > 0){
            StorageService.setItem("user", StorageService.stringify(userData))
        }
    }, [userData])

    return (
        <div className="show-tutorial-modal">
            <div className="show-tutorial-modal__title-container">
                <p className="show-tutorial-modal__title">{translation.viedoWelcomeText}</p>
                <div className="show-tutorial-modal__close-icon" onClick={closeModal}>
                    <CloseIcon />
                </div>
            </div>
            <div className="show-tutorial-modal__content-container">
                <div className="show-tutorial-modal__explanation-info">
                    <p className="show-tutorial-modal__info">{role === RoleType.USER ? translation.videoExplanationText : translation.tutorial_org_welcome_text}</p>
                </div>
                <div className="show-tutorial-modal__meta">
                    <div className="show-tutorial-modal__meta-item">{translation.onboardingDurationHint}</div>
                </div>
                <div className="show-tutorial-modal__buttons-container">
                    <div className="show-tutorial-modal__button-size">
                        <Button
                            onClick={handleStartHere}
                            disabled={isSubmitting}
                            className="button button--border-radius-05 button--color-gold button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-large button--font-weight-400  "
                        >
                            {translation.startTutorial}
                        </Button>
                    </div>
                    <div className="show-tutorial-modal__button-size">
                        <Button
                            onClick={handleSkip}
                            disabled={isSubmitting}
                            className="button button--border-radius-05 button--color-red button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-large button--font-weight-400  "
                        >
                            {translation.skipTutorial}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ShowTutorialModal
